import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getProduct, ProductDetailResult } from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import {
    amazonURL, FIELD_LABEL, FIELD_SKIP, fmtDate, fmtDateTime, fmtDims, fmtWeight, MaterialChips, materialList, parseJSON, ProductImg, ScopeBadge, targetText,
} from './productLabels';

// 商品采集详情: 图集 · 关键信息 · 全部采集字段 (key-value, 含品牌特有属性) · 特性要点 · 变体 · 榜单历史 · 溯源.
// 详情以 key-value 展示该商品采到的全部字段 (需求原文): 缺失字段直接不列, 不假设每条都有.

interface Variation { asin?: string; attributes?: { dimension?: string; value?: string }[]; image?: string }

function Section({ title, hint, children, right }: { title: string; hint?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
    return (
        <div className="card p-4">
            <div className="flex items-baseline gap-2 mb-2">
                <div className="text-sm font-semibold text-slate-700">{title}</div>
                {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
                {right && <div className="ml-auto">{right}</div>}
            </div>
            {children}
        </div>
    );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-3 border-b border-slate-50 py-1.5 text-xs">
            <span className="text-slate-400 shrink-0">{label}</span>
            <span className="text-slate-700 text-right break-words min-w-0">{children}</span>
        </div>
    );
}

const fmtValue = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'string') {
        const j = parseJSON<unknown>(v, undefined);
        if (j !== undefined && typeof j === 'object') return JSON.stringify(j);
        return v;
    }
    return String(v);
};

function ProductDetail() {
    const { asin } = useParams<{ asin: string }>();
    const [data, setData] = useState<ProductDetailResult | null>(null);
    const [error, setError] = useState('');
    const [active, setActive] = useState(0);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setData(null);
        setError('');
        setActive(0);
        getProduct(asin).then((r) => { if (alive) setData(r); }).catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [asin]);

    const p = data?.product;
    const images = useMemo(() => (data?.images || []).filter((i) => i.imageUrl), [data]);
    const mats = useMemo(() => (p ? materialList(p) : []), [p]);
    const features = useMemo(() => parseJSON<string[]>(p?.features, []), [p]);
    const variations = useMemo(() => parseJSON<Variation[]>(p?.variations, []), [p]);
    // 全部采集字段: 去掉已专门展示的和空值.
    const fields = useMemo(() => {
        if (!p) return [] as [string, string][];
        return Object.entries(p)
            .filter(([k]) => !FIELD_SKIP.has(k))
            .map(([k, v]) => [k, fmtValue(v)] as [string, string])
            .filter(([, v]) => v !== '');
    }, [p]);
    const missing = useMemo(() => (p ? Object.entries(p).filter(([k, v]) => !FIELD_SKIP.has(k) && (v === null || v === '')).length : 0), [p]);
    const variationDims = useMemo(() => {
        const s = new Set<string>();
        variations.forEach((v) => v.attributes?.forEach((a) => a.dimension && s.add(a.dimension)));
        return Array.from(s);
    }, [variations]);
    const imgByFile = useMemo(() => new Map(images.map((i) => [i.filename, i.imageUrl as string])), [images]);
    const known = useMemo(() => new Set(data?.knownVariantAsins || []), [data]);

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!p) return <div className="text-sm text-slate-400 p-6">加载中…</div>;

    const main = images[active]?.imageUrl || images[0]?.imageUrl;

    return (
        <div className="space-y-4">
            <div>
                <Link to="/assets/products?tab=table" className="text-xs text-blue-600 hover:underline">← 返回 商品采集</Link>
                <div className="flex items-start justify-between gap-4 mt-1">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-1">
                            <span className="font-semibold text-slate-600">{p.brand || '未知品牌'}</span>
                            <span>·</span><span>{targetText(p.target)}</span>
                            {p.bestSellerRank != null && <><span>·</span><span className="font-mono">榜单 #{p.bestSellerRank}</span></>}
                            <ScopeBadge scope={p.inScope} />
                        </div>
                        <div className="text-lg font-extrabold text-slate-900 tracking-tight leading-snug">{p.title || '无标题'}</div>
                        <div className="text-xs text-slate-400 mt-1 truncate" title={p.categoryPath || ''}>{p.categoryPath || '—'}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">{p.asin}</span>
                        <a href={amazonURL(p.asin)} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700">原商品链接 ↗</a>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-[400px_1fr] gap-4 items-start">
                {/* 左: 图集 + 关键信息 */}
                <div className="space-y-3">
                    <div className="card p-3">
                        {main ? (
                            <ProductImg src={main} className="w-full rounded-lg object-contain bg-white cursor-zoom-in border border-slate-100" style={{ aspectRatio: '1 / 1' }} onClick={() => setZoomSrc(main)} />
                        ) : (
                            <div className="w-full rounded-lg bg-slate-50 flex items-center justify-center text-xs text-slate-400" style={{ aspectRatio: '1 / 1' }}>没有采到商品图</div>
                        )}
                        {images.length > 1 && (
                            <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
                                {images.map((im, i) => (
                                    <ProductImg key={im.filename} src={im.imageUrl as string}
                                        className={`rounded-md object-contain bg-white cursor-pointer ring-2 shrink-0 ${i === active ? 'ring-slate-800' : 'ring-slate-100 hover:ring-slate-300'}`}
                                        style={{ width: 56, height: 56 }} onClick={() => setActive(i)} title={`${im.width ?? '?'}×${im.height ?? '?'}${im.status === 'small' ? ' · 疑似占位图' : ''}`} />
                                ))}
                            </div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-2 flex justify-between">
                            <span>{images.length} 张{p.imageCount != null && p.imageCount > images.length ? ` · 源标 ${p.imageCount} 张` : ''} · 灰色占位 = 采集器尚未上传到本平台存储</span>
                            {images[active] && <span className="font-mono">{images[active].width ?? '?'}×{images[active].height ?? '?'}</span>}
                        </div>
                    </div>

                    <div className="card p-4">
                        <div className="text-sm font-semibold text-slate-700 mb-1">关键信息</div>
                        <Fact label="材质"><MaterialChips items={mats} max={8} size="md" /></Fact>
                        <Fact label="颜色">{p.color || <span className="text-slate-300">—</span>}</Fact>
                        <Fact label="尺寸规格">{p.size || <span className="text-slate-300">—</span>}</Fact>
                        <Fact label="实物尺寸">{fmtDims(p.itemLengthMm, p.itemWidthMm, p.itemHeightMm)}</Fact>
                        <Fact label="实物重量">{fmtWeight(p.itemWeightG)}</Fact>
                        {p.thicknessMm != null && <Fact label="厚度 (推断)">{p.thicknessMm} mm</Fact>}
                        <Fact label="商品类型">{p.binding || <span className="text-slate-300">—</span>}</Fact>
                        <Fact label="月销量">{p.monthlySold != null ? p.monthlySold.toLocaleString() : <span className="text-slate-300">— (Keepa 无数据)</span>}</Fact>
                        <Fact label="上市时间">{fmtDate(p.listedSince)}</Fact>
                        {p.model && <Fact label="型号">{p.model}</Fact>}
                        {p.manufacturer && <Fact label="制造商">{p.manufacturer}</Fact>}
                    </div>

                    <div className="card p-4 text-xs text-slate-500 space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">溯源</div>
                        <div>采集目标: <span className="font-mono">{p.target || '—'}</span> · 归属判定 <ScopeBadge scope={p.inScope} /></div>
                        <div>来源榜单节点: <span className="font-mono">{p.sourceNodeId ?? '—'}</span>{data?.sourceNode?.nodeName && <> · {data.sourceNode.nodeName}</>}</div>
                        {data?.sourceNode?.fullPath && <div className="text-slate-400 break-words">节点路径: {data.sourceNode.fullPath}</div>}
                        <div>发现于 <span className="font-mono">{fmtDateTime(p.discoveredAt)}</span> · 拉详情 <span className="font-mono">{fmtDateTime(p.fetchedAt)}</span></div>
                        {p.brandStoreUrl && <div className="truncate">品牌店: <a href={p.brandStoreUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{p.brandStoreName || p.brandStoreUrl}</a></div>}
                    </div>
                </div>

                {/* 右: 特性 / 全部字段 / 变体 / 榜单历史 */}
                <div className="space-y-4">
                    {features.length > 0 && (
                        <Section title="特性要点" hint={`features · ${features.length} 条`}>
                            <ul className="text-xs text-slate-600 space-y-1.5 list-disc pl-4 leading-relaxed">
                                {features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        </Section>
                    )}

                    <Section title="全部采集字段" hint={`采到 ${fields.length} 个 · 缺失 ${missing} 个未列出`}>
                        <div className="grid gap-x-6 gap-y-0.5 text-xs" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                            {fields.map(([k, v]) => (
                                <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1.5">
                                    <span className="text-slate-400 shrink-0" title={k}>{FIELD_LABEL[k] || k}</span>
                                    <span className="text-slate-700 text-right break-all font-mono min-w-0" title={v}>{v.length > 120 ? `${v.slice(0, 120)}…` : v}</span>
                                </div>
                            ))}
                        </div>
                        {p.description && (
                            <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded p-3 leading-relaxed whitespace-pre-wrap">{p.description}</div>
                        )}
                    </Section>

                    {variations.length > 0 && (
                        <Section title="颜色 / 尺寸变体" hint={`${variations.length} 个在售变体 · 本库有 ${known.size} 个 (蓝色可点) · 其余只能去亚马逊看`}>
                            <div className="overflow-x-auto" style={{ maxHeight: 360 }}>
                                <table className="w-full text-xs">
                                    <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 sticky top-0 bg-white">
                                        <th className="py-1.5 pr-2">图</th><th className="py-1.5 pr-2">ASIN</th>
                                        {variationDims.map((d) => <th key={d} className="py-1.5 pr-2 whitespace-nowrap">{d}</th>)}
                                    </tr></thead>
                                    <tbody>
                                        {variations.slice(0, 200).map((v, i) => (
                                            <tr key={`${v.asin}-${i}`} className="border-b border-slate-50">
                                                <td className="py-1 pr-2">
                                                    {v.image && imgByFile.get(v.image) ? (
                                                        <img src={imgByFile.get(v.image)} alt="" loading="lazy" className="rounded object-contain bg-white ring-1 ring-slate-100 cursor-zoom-in" style={{ width: 28, height: 28 }} onClick={() => setZoomSrc(imgByFile.get(v.image as string) || null)} />
                                                    ) : <span className="text-slate-300">—</span>}
                                                </td>
                                                <td className="py-1 pr-2 font-mono whitespace-nowrap">
                                                    {!v.asin ? '—' : v.asin === p.asin ? (
                                                        <span className="text-slate-800 font-semibold">{v.asin} <span className="text-[10px] text-slate-400 font-sans font-normal">当前</span></span>
                                                    ) : known.has(v.asin) ? (
                                                        <Link to={`/assets/products/${v.asin}`} className="text-blue-600 hover:underline">{v.asin}</Link>
                                                    ) : (
                                                        <span className="text-slate-500" title="未被榜单发现, 本库没有独立记录">
                                                            {v.asin} <a href={amazonURL(v.asin)} target="_blank" rel="noreferrer" className="text-[10px] text-slate-400 hover:text-slate-700 font-sans">亚马逊 ↗</a>
                                                        </span>
                                                    )}
                                                </td>
                                                {variationDims.map((d) => <td key={d} className="py-1 pr-2 text-slate-600">{v.attributes?.find((a) => a.dimension === d)?.value || <span className="text-slate-300">—</span>}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {variations.length > 200 && <div className="text-[11px] text-slate-400 mt-1">只显示前 200 个变体，共 {variations.length} 个。</div>}
                            </div>
                            <div className="text-[11px] text-slate-400 mt-2">变体 ASIN 只有被榜单发现过的才在本库里有独立记录（蓝色可点）；灰色的本库没有，给了亚马逊外链。</div>
                        </Section>
                    )}

                    <Section title="榜单历史" hint="每次拉榜一条 · 位次属于「某次拉榜」不属于商品">
                        {(data?.snapshots.length ?? 0) === 0 ? <div className="text-xs text-slate-400">没有榜单快照。</div> : (
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-1.5">拉榜时间</th><th className="py-1.5">榜单节点</th><th className="py-1.5 text-right">位次</th>
                                </tr></thead>
                                <tbody>
                                    {data?.snapshots.map((s) => (
                                        <tr key={`${s.nodeId}-${s.pulledAt}`} className="border-b border-slate-50">
                                            <td className="py-1.5 font-mono text-slate-600">{s.pulledAt}</td>
                                            <td className="py-1.5 text-slate-600">{s.nodeName || s.nodeId} <span className="text-slate-400">· {targetText(s.target)}</span></td>
                                            <td className="py-1.5 text-right font-mono font-semibold text-slate-800">#{s.bsrRank}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Section>
                </div>
            </div>
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default ProductDetail;
